import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as companyApi from '../Api/companyApi';
import {
	hasNeverHadSubscriptionPlan,
	matchPlanFromSubscription,
} from '../components/Auth/SubscriptionPlan/subscriptionPlanMatrixContext';
import { usePlans } from './usePlan';

const normalizeSubscription = (subscriptionData) => {
	if (!subscriptionData) {
		return {
			hasActiveSubscription: false,
			hasNeverHadPlan: true,
			status: null,
			planName: null,
			stripeCustomerId: null,
			paymentSubscriptionIntentId: null,
		};
	}

	const normalized = {
		id: subscriptionData._id,
		status: subscriptionData.status,
		planName: subscriptionData.planName,
		planId: subscriptionData.planId,
		purchaseSource: subscriptionData.purchaseSource,
		stripeCustomerId: subscriptionData.stripeCustomerId,
		paymentSubscriptionIntentId: subscriptionData.paymentSubscriptionIntentId,
		subscriptionPriceId: subscriptionData.subscriptionPriceId,
		subscriptionId: subscriptionData.subscriptionId,
		amount: subscriptionData.amount,
		currency: subscriptionData.currency,
		billingCycle: subscriptionData.billingCycle,
		nextBillingDate: subscriptionData.nextBillingDate,
		dueToBeCanceledDate: subscriptionData.dueToBeCanceledDate,
		isActive: subscriptionData.status === 'active',
		isTrialing: subscriptionData.status === 'trialing',
		isLifetime: subscriptionData.billingCycle === 'lifetime',
		hasActiveSubscription:
			subscriptionData.status === 'active' &&
			(subscriptionData.billingCycle === 'lifetime'
				? Boolean(
						subscriptionData.planId ||
							subscriptionData.paymentSubscriptionIntentId,
					)
				: Boolean(
						subscriptionData.stripeCustomerId &&
							subscriptionData.paymentSubscriptionIntentId &&
							(!subscriptionData.dueToBeCanceledDate ||
								new Date(subscriptionData.dueToBeCanceledDate) > new Date()),
					)),
		subscriptionStatus: subscriptionData.status,
		mongoId: subscriptionData._id,
	};

	return {
		...normalized,
		hasNeverHadPlan: hasNeverHadSubscriptionPlan(normalized),
	};
};

export const useSubscription = (companyId) => {
	const subscriptionQuery = useQuery({
		queryKey: ['subscription', companyId],
		queryFn: async () => {
			if (!companyId) return null;
			const companyResponse = await companyApi.getCompanyById(companyId);
			return companyResponse.data?.companySubscriptionPlan || null;
		},
		enabled: !!companyId,
		staleTime: 5 * 60 * 1000,
		select: normalizeSubscription,
	});

	const { data: plans } = usePlans({ enabled: !!companyId && !!subscriptionQuery.data });

	const data = useMemo(() => {
		if (!subscriptionQuery.data) return subscriptionQuery.data;

		const matchedPlan = matchPlanFromSubscription(plans, subscriptionQuery.data);
		if (!matchedPlan) return subscriptionQuery.data;

		return {
			...subscriptionQuery.data,
			planName: matchedPlan.planName || subscriptionQuery.data.planName,
			planId: matchedPlan._id || subscriptionQuery.data.planId,
		};
	}, [subscriptionQuery.data, plans]);

	return {
		...subscriptionQuery,
		data,
	};
};
